import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { UseQueryOptions } from 'react-query';
import { Box } from '@material-ui/core';

import { GetContentQuery, useGetContentQuery, useGetSharePreviewInfoQuery } from 'api';
import { Events } from 'analytics';
import { useMonitorPost, useNotify } from 'hooks';
import { ContentProvider, SharePreviewProvider } from 'providers';
import { Loaders } from 'components';

import { getVideo } from '../helpers';
import Permalink from './Permalink';

const REFETCH_INTERVAL = 1000 * 5;

const ContentLoader: FC = () => {
  const { content_id } = useParams<{ content_id: string }>();
  const notify = useNotify();
  const { state, dispatch } = useMonitorPost();

  const queryOptions: UseQueryOptions<GetContentQuery> = !state.has(content_id)
    ? {}
    : {
        refetchInterval: REFETCH_INTERVAL,
        isDataEqual: (oldData, newData) => {
          const video = getVideo(newData.getContent);
          if (video?.status === 'complete') {
            dispatch({ type: 'unwatch', id: content_id });
          }
          return false;
        },
      };

  const { data, isLoading } = useGetContentQuery({ content_id, display_only: true }, queryOptions);

  const { data: sharePreviewData, isLoading: isPreviewLoading } = useGetSharePreviewInfoQuery(
    { content_id },
    {
      onError: notify.queryError,
    }
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('es_source')) {
      window?.analytics?.track(Events.RepostClicked, {
        referral_source: searchParams.get('es_source'),
      });
    }
  }, []);

  if (isLoading || isPreviewLoading) {
    return <Loaders.ContentShare />;
  }

  if (!sharePreviewData?.getSharePreviewInfo) {
    return null;
  }

  return (
    <Box style={{ height: '100vh' }}>
      <ContentProvider value={data?.getContent}>
        <SharePreviewProvider value={sharePreviewData?.getSharePreviewInfo}>
          <Permalink />
        </SharePreviewProvider>
      </ContentProvider>
    </Box>
  );
};

export default ContentLoader;
